import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiList, apiPatch, apiPost } from '@/lib/api';
import type {
  ActivityEntry,
  Board,
  Company,
  CompanyOption,
  Contact,
  ContactOption,
  Deal,
  DuplicateCheck,
  Lead,
  LeadBoard,
  Pipeline,
  PipelineEntityType,
  PipelineInput,
  Quote,
  RequirableField,
  Task,
  TaskRemark,
} from './types';

/** Drops empty params so URLs stay readable and query caches hit. */
function clean<T extends Record<string, unknown>>(params: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null),
  ) as Partial<T>;
}

type ListParams = Record<string, unknown>;

// ── Leads ────────────────────────────────────────────────────────────────────

export function useLeads(params: ListParams) {
  return useQuery({
    queryKey: ['leads', params],
    queryFn: () => apiList<Lead[]>('/leads', { params: clean(params) }),
    placeholderData: (previous) => previous,
  });
}

export function useLead(id: string | undefined) {
  return useQuery({
    queryKey: ['leads', id],
    queryFn: () => apiGet<Lead>(`/leads/${id}`),
    enabled: Boolean(id),
  });
}

/** Live duplicate check while the capture form is being filled in. */
export function useDuplicateCheck(params: {
  email?: string;
  phone?: string;
  companyName?: string;
  name?: string;
  excludeId?: string;
  enabled?: boolean;
}) {
  const { enabled = true, ...query } = params;
  const hasSomething = Boolean(query.email || query.phone || query.companyName || query.name);

  return useQuery({
    queryKey: ['leads', 'duplicates', query],
    queryFn: () => apiGet<DuplicateCheck>('/leads/duplicates', { params: clean(query) }),
    enabled: enabled && hasSomething,
    staleTime: 10_000,
  });
}

export function useSaveLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? apiPatch<Lead>(`/leads/${id}`, body) : apiPost<Lead>('/leads', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['leads'] }),
  });
}

export function useConvertLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      apiPost<{ companyId: string; contactId: string; dealId: string | null }>(
        `/leads/${id}/convert`,
        body,
      ),
    onSuccess: () => {
      // Conversion touches four collections at once.
      for (const key of ['leads', 'companies', 'contacts', 'deals']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}

export function useBulkLeads() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPost<{ updated: number }>('/leads/bulk', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['leads'] }),
  });
}

export function useDeleteLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/leads/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['leads'] }),
  });
}

/** The lead board: one lead pipeline's columns with the leads in each. */
export function useLeadBoard(params: ListParams) {
  return useQuery({
    queryKey: ['leads', 'board', params],
    queryFn: () => apiGet<LeadBoard>('/leads/board', { params: clean(params) }),
    placeholderData: (previous) => previous,
  });
}

export function useMoveLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      apiPost<Lead>(`/leads/${id}/move`, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['leads'] }),
  });
}

/** Incoming → onto the board. */
export function useAcceptLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; stageId?: string; ownerId?: string }) =>
      apiPost<Lead>(`/leads/${id}/accept`, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['leads'] }),
  });
}

/** Incoming → declined (spam, wrong number). */
export function useDeclineLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => apiPost(`/leads/${id}/decline`, { reason }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['leads'] }),
  });
}

/** Incoming → folded into a lead that already exists. */
export function useMergeLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, targetLeadId }: { id: string; targetLeadId: string }) =>
      apiPost<Lead>(`/leads/${id}/merge`, { targetLeadId }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['leads'] }),
  });
}

// ── Companies ────────────────────────────────────────────────────────────────

export function useCompanies(params: ListParams) {
  return useQuery({
    queryKey: ['companies', params],
    queryFn: () => apiList<Company[]>('/companies', { params: clean(params) }),
    placeholderData: (previous) => previous,
  });
}

export function useContact(id: string | undefined) {
  return useQuery({
    queryKey: ['contacts', id],
    queryFn: () => apiGet<Contact>(`/contacts/${id}`),
    enabled: Boolean(id),
  });
}

export function useCompany(id: string | undefined) {
  return useQuery({
    queryKey: ['companies', id],
    queryFn: () => apiGet<Company & { contacts: Contact[]; deals: Deal[] }>(`/companies/${id}`),
    enabled: Boolean(id),
  });
}

export function useCompanyOptions(search?: string) {
  return useQuery({
    queryKey: ['companies', 'options', search],
    queryFn: () => apiGet<CompanyOption[]>('/companies/options', { params: clean({ q: search }) }),
    staleTime: 60_000,
  });
}

export function useSaveCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? apiPatch<Company>(`/companies/${id}`, body) : apiPost<Company>('/companies', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['companies'] }),
  });
}

export function useDeleteCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/companies/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['companies'] }),
  });
}

// ── Contacts ─────────────────────────────────────────────────────────────────

export function useContacts(params: ListParams, enabled = true) {
  return useQuery({
    queryKey: ['contacts', params],
    queryFn: () => apiList<Contact[]>('/contacts', { params: clean(params) }),
    placeholderData: (previous) => previous,
    // A company that does not exist yet has nobody at it, and asking without
    // a companyId would list every contact in the organisation.
    enabled,
  });
}

export function useContactOptions(companyId?: string, search?: string) {
  return useQuery({
    queryKey: ['contacts', 'options', companyId, search],
    queryFn: () =>
      apiGet<ContactOption[]>('/contacts/options', { params: clean({ companyId, q: search }) }),
    staleTime: 60_000,
  });
}

export function useSaveContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? apiPatch<Contact>(`/contacts/${id}`, body) : apiPost<Contact>('/contacts', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/contacts/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['contacts'] }),
  });
}

// ── Pipelines ────────────────────────────────────────────────────────────────

/** The boards for one kind of record. Deals unless asked for leads. */
export function usePipelines(entityType: PipelineEntityType = 'deal') {
  return useQuery({
    queryKey: ['pipelines', entityType],
    queryFn: () => apiGet<Pipeline[]>('/pipelines', { params: { entityType } }),
    staleTime: 5 * 60 * 1000,
  });
}

/** The deal fields a stage can insist on before a deal leaves it. */
export function useRequirableFields() {
  return useQuery({
    queryKey: ['pipelines', 'requirable-fields'],
    queryFn: () => apiGet<RequirableField[]>('/pipelines/requirable-fields'),
    staleTime: 10 * 60 * 1000,
  });
}

/** Creates without an id, otherwise PATCHes. Either way the board reshapes. */
export function useSavePipeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: PipelineInput) =>
      id ? apiPatch<Pipeline>(`/pipelines/${id}`, body) : apiPost<Pipeline>('/pipelines', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pipelines'] });
      void queryClient.invalidateQueries({ queryKey: ['deals'] });
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useDeletePipeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/pipelines/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pipelines'] });
      void queryClient.invalidateQueries({ queryKey: ['deals'] });
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

// ── Deals ────────────────────────────────────────────────────────────────────

export function useDeals(params: ListParams) {
  return useQuery({
    queryKey: ['deals', params],
    queryFn: () => apiList<Deal[]>('/deals', { params: clean(params) }),
    placeholderData: (previous) => previous,
  });
}

export function useBoard(params: ListParams) {
  return useQuery({
    queryKey: ['deals', 'board', params],
    queryFn: () => apiGet<Board>('/deals/board', { params: clean(params) }),
    placeholderData: (previous) => previous,
  });
}

export function useDeal(id: string | undefined) {
  return useQuery({
    queryKey: ['deals', id],
    queryFn: () => apiGet<Deal & { stageHistory: unknown[]; quotes: unknown[] }>(`/deals/${id}`),
    enabled: Boolean(id),
  });
}

export function useLostReasons() {
  return useQuery({
    queryKey: ['deals', 'lost-reasons'],
    queryFn: () => apiGet<string[]>('/deals/lost-reasons'),
    staleTime: 10 * 60 * 1000,
  });
}

export function useSaveDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? apiPatch<Deal>(`/deals/${id}`, body) : apiPost<Deal>('/deals', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['deals'] }),
  });
}

export function useMoveDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      apiPost<Deal>(`/deals/${id}/move`, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['deals'] }),
  });
}

export function useDeleteDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/deals/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['deals'] }),
  });
}

// ── Quotes ───────────────────────────────────────────────────────────────────

export function useQuotes(params: ListParams) {
  return useQuery({
    queryKey: ['quotes', params],
    queryFn: () => apiList<Quote[]>('/quotes', { params: clean(params) }),
    placeholderData: (previous) => previous,
  });
}

export function useQuote(id: string | undefined) {
  return useQuery({
    queryKey: ['quotes', id],
    queryFn: () => apiGet<Quote>(`/quotes/${id}`),
    enabled: Boolean(id),
  });
}

export function useSaveQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost<Quote>('/quotes', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['quotes'] }),
  });
}

export function useReviseQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<Quote>(`/quotes/${id}/revise`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['quotes'] }),
  });
}

export function useSendQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<Quote>(`/quotes/${id}/send`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['quotes'] }),
  });
}

export function useDeleteQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/quotes/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['quotes'] }),
  });
}

// ── Tasks & timeline ─────────────────────────────────────────────────────────

export function useTasks(params: ListParams) {
  return useQuery({
    queryKey: ['tasks', params],
    queryFn: () => apiList<Task[]>('/tasks', { params: clean(params) }),
    placeholderData: (previous) => previous,
  });
}

/**
 * A month of the calendar. Not `useTasks`: that is paged, and a calendar
 * showing the first page of a busy month is a calendar telling lies.
 */
export function useTaskCalendar(from: string, to: string) {
  return useQuery({
    queryKey: ['tasks', 'calendar', from, to],
    queryFn: () => apiGet<{ tasks: Task[] }>('/tasks/calendar', { params: { from, to } }),
    placeholderData: (previous) => previous,
  });
}

export function useMyDay() {
  return useQuery({
    queryKey: ['tasks', 'my-day'],
    queryFn: () =>
      apiGet<{
        overdue: Task[];
        today: Task[];
        upcoming: Task[];
        /** Most recently finished first. */
        completed: Task[];
        unreadNotifications: number;
      }>('/tasks/my-day'),
  });
}

export function useSaveTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? apiPatch<Task>(`/tasks/${id}`, body) : apiPost<Task>('/tasks', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['timeline'] });
      void queryClient.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

/** One task by id — what a notification opens. */
export function useTask(taskId: string | undefined) {
  return useQuery({
    queryKey: ['tasks', 'one', taskId],
    queryFn: () => apiGet<Task>(`/tasks/${taskId}`),
    enabled: Boolean(taskId),
    retry: false,
  });
}

/** The updates on one task, oldest first. */
export function useTaskRemarks(taskId: string | undefined) {
  return useQuery({
    queryKey: ['tasks', 'remarks', taskId],
    queryFn: () => apiGet<TaskRemark[]>(`/tasks/${taskId}/remarks`),
    enabled: Boolean(taskId),
  });
}

/**
 * Posts an update — a remark, a status move, or both. Goes through its own
 * route so the person given the task can report on it without the right to
 * edit tasks in general.
 */
export function useAddTaskRemark() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, ...body }: { taskId: string; body?: string; status?: Task['status'] }) =>
      apiPost<{ remark: TaskRemark; task: Task }>(`/tasks/${taskId}/remarks`, body),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
      void queryClient.invalidateQueries({ queryKey: ['tasks', 'remarks', variables.taskId] });
      void queryClient.invalidateQueries({ queryKey: ['timeline'] });
    },
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/tasks/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['tasks'] }),
  });
}

export function useTimeline(entityType: string | undefined, entityId: string | undefined) {
  return useQuery({
    queryKey: ['timeline', entityType, entityId],
    queryFn: () =>
      apiGet<{ activities: ActivityEntry[]; tasks: Task[] }>(`/timeline/${entityType}/${entityId}`),
    enabled: Boolean(entityType && entityId),
  });
}

export function useLogActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost<ActivityEntry>('/timeline', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timeline'] });
      void queryClient.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}
